import type { LDClient, LDContext } from 'launchdarkly-vue-client-sdk';
import type { SanitizedAuth0User } from '@/plugins/launchDarkly/utils';

export class FeatureFlagsService {
  private static instance: FeatureFlagsService;
  private ldClient: LDClient | null = null;

  public static getInstance(): FeatureFlagsService {
    if (!FeatureFlagsService.instance) {
      FeatureFlagsService.instance = new FeatureFlagsService();
    }
    return FeatureFlagsService.instance;
  }

  public setClient(client: LDClient): void {
    this.ldClient = client;
  }

  public buildUserContext(user: SanitizedAuth0User | null | undefined, isAuthenticated: boolean): LDContext {
    const isKnownUser = Boolean(isAuthenticated && user?.sub);
    const context: LDContext = {
      kind: 'user',
      key: isKnownUser ? (user?.sub as string) : 'anonymous',
      anonymous: !isKnownUser,
    };

    // Return minimal context for anonymous users
    if (!isKnownUser || !user) {
      return context;
    }

    // Map all SanitizedAuth0User properties to LaunchDarkly context for flexible targeting
    const attributes = {
      accessLevel: user.accessLevel,
      accountStatus: user.accountStatus,
      companyName: user.companyName,
      countryCode: user.countryCode,
      currency: user.currency,
      email: user.email,
      emailVerified: user.emailVerified,
      name: user.name,
      organizationId: user.organizationId,
      organizationName: user.organizationName,
      ownerRole: user.ownerRole,
      permissions: user.permissions?.length ? user.permissions : undefined,
      roles: user.roles?.length ? user.roles : undefined,
      timezone: user.timezone,
      vaultnOwnerId: user.vaultnOwnerId,
    };

    // Add only defined values to context
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        context[key] = value;
      }
    });

    // Mark sensitive PII fields as private to prevent storage while still allowing targeting
    context._meta = {
      privateAttributes: ['email', 'name', 'companyName', 'organizationName', 'permissions', 'roles'],
    };

    return context;
  }

  public async identify(context: LDContext) {
    if (!this.ldClient) {
      console.warn('LD client not initialized');
      return;
    }
    return this.ldClient.identify(context);
  }
}